import { useState } from "react";
import { motion } from "framer-motion";
import { SearchCheck, FileText, Wrench } from "lucide-react";
const PILLARS = [
  {
    number: "01",
    tag: "Da Vinci CRD (Coverage Check)",
    title: "Coverage Requirements Discovery",
    icon: SearchCheck,
    description:
      "Evaluates treatment necessity at the point of order within the EHR. Automatically queries payer rules via CDS Hooks to determine whether prior authorization is mandated before submission.",
    points: [
      "Sub-second CDS Hook evaluation",
      "Eliminates unnecessary authorization requests",
      "Native Epic & Cerner integration",
    ],
  },
  {
    number: "02",
    tag: "Da Vinci DTR (Documentation)",
    title: "Documentation Templates & Rules",
    icon: FileText,
    description:
      "Automates clinical documentation retrieval by executing payer CQL (Clinical Quality Language) against the patient's FHIR chart, pre-filling questionnaires without manual chart scraping.",
    points: [
      "Automated CQL clinical extraction",
      "SMART-on-FHIR dynamic forms",
      "Zero manual clinical re-entry",
    ],
  },
  {
    number: "03",
    tag: "Da Vinci PAS & Camunda 8",
    title: "Prior Authorization Support",
    icon: Wrench,
    description:
      "Bundles the completed claim and clinical rationale into FHIR Claim resources. Camunda 8 orchestrates payer adjudication, automated DMN policy checks, and returns real-time approvals.",
    points: [
      "Camunda 8 Zeebe event streaming",
      "X12 278 translation & straight-through pass",
      "Under 15-second decision latency",
    ],
  },
];
export default function PriorHpillars() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  return (
    <section className="relative w-full lg:py-24 md:py-16 py-10 bg-[#000036] overflow-hidden">
      <div className="mx-auto 3xl:w-4/5 lg:w-11/12 px-6 lg:px-12 relative z-10">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md mb-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">
              CORE ARCHITECTURE
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
            The 3 Pillars of{" "}
            <span className="italic font-serif font-normal text-[#62d4f7]">
              Automated
            </span>{" "}
            Prior Auth
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white leading-relaxed max-w-2xl">
            Bridging healthcare interoperability with HL7 FHIR Da Vinci
            standards and high-throughput Camunda 8 process orchestration.
          </p>
        </div>
        <div className="rounded-xl border border-black/10 bg-white backdrop-blur-2xl shadow-[0_12px_45px_rgba(0,0,0,0.035)] overflow-hidden">
          <div className="lg:grid hidden grid-cols-3 border-b border-slate-200/80 bg-slate-50/50">
            {PILLARS.map((step, idx) => (
              <div
                key={step.number}
                className={`relative px-6 py-4 flex items-center md:block justify-between transition-colors duration-300 ${
                  idx !== 2 ? "border-r border-slate-200/80" : ""
                } ${hoveredIndex === idx ? "bg-white" : ""}`}
              >
                <span className="text-xs font-bold tracking-widest text-slate-400">
                  PILLAR // 0{idx + 1}
                </span>

                <span
                  className={`h-2 w-2 rounded-full transition-all duration-300 ${
                    hoveredIndex === idx
                      ? "bg-slate-950 scale-125"
                      : "bg-slate-300"
                  }`}
                />
                {hoveredIndex === idx && (
                  <motion.div
                    layoutId="pillarMonolithIndicator"
                    className="absolute top-0 inset-x-0 h-[2.5px] bg-[#000036]"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200/80">
            {PILLARS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="group relative p-6 sm:p-10 flex flex-col justify-between transition-all duration-300 hover:bg-white/90"
                >
                  <div>
                    <div className="flex items-start justify-between mb-8">
                      <span className="font-serif text-7xl sm:text-8xl font-light tracking-tighter text-[#000036] transition-all duration-500 group-hover:text-black group-hover:-translate-y-1">
                        {step.number}
                      </span>
                      <div className="h-14 w-14 rounded-2xl bg-white border border-black/5 shadow-xs flex items-center justify-center text-slate-800 transition-all duration-300 group-hover:bg-[#000036] group-hover:text-white group-hover:rotate-[-6deg]">
                        <Icon size={30} strokeWidth={1.75} />
                      </div>
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 block mb-2">
                      {step.tag}
                    </span>
                    <h3 className="text-2xl font-bold tracking-tight text-[#000036] leading-snug mb-4">
                      {step.title}
                    </h3>
                    <p className="text-base leading-relaxed text-slate-600 font-normal mb-8">
                      {step.description}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-slate-100/90 flex flex-col gap-2">
                    {step.points.map((point, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-sm font-medium text-slate-700 font-mono"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#62d4f7]" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
